import { UploadType } from './../merchant-products.component';
import { UOMCategory } from './../../../../../models/product/uom-category';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AppDataService, ProductService } from 'app/services';
import { MessageService } from 'primeng/api';
import { ProductTagModel } from './product-detail-form/product-detail-form.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'add-product-single-dialog',
  templateUrl: './add-product-single-dialog.component.html',
  styleUrls: ['./add-product-single-dialog.component.scss'],
  providers: [MessageService],
})
export class AddProductSingleDialogComponent implements OnInit {
  // Product Detail form
  productDetailFormGroup = this.formBuilder.group({
    Name: ['', Validators.required],
    Category: [{}, Validators.required],
    Subcategory: [{}, Validators.required],
    SKU: [''],
    Tags: [''],
    Description: ['', Validators.required],
    ImageUrl: ['', Validators.required],
  });
  // Pricing form
  pricingFormGroup = this.formBuilder.group({
    CostPrice: [0],
    SellingPrice: ['', Validators.required],
    VatAmount: [0, Validators.required],
    DisplayedPrice: [0, [Validators.required]],
    IsVatable: [false, Validators.required],
  });

  selectedTagList = new Set<ProductTagModel>(); // Monitor selected tags

  // UOM form
  uomFormGroup = this.formBuilder.group({
    UOM: ['', Validators.required],
    Measurement: ['', Validators.required],
    Units: ['', Validators.required],
  });

  // Store Availability form auto complete
  availabilityControl = new FormControl('');
  storeSelectedSet = new Set<any>(); // Monitor child added to set

  // Utilities
  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    private appData: AppDataService,
    private messageService: MessageService,
    private productService: ProductService,
    private dialogRef: MatDialogRef<AddProductSingleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.pricingFormGroup.get('DisplayedPrice')?.disable();
  }

  onHandleCreateProduct() {
    // Validate all forms before proceeding
    if (!this.validateAllForms()) {
      return;
    }

    this.loading = true;
    const selectedCategory: any = this.productDetailFormGroup.value.Category;
    const selectedSubcategory: any = this.productDetailFormGroup.value.Subcategory;
    const selectedTags: ProductTagModel[] | any = [
      ...Array.from(this.selectedTagList),
    ];

    const selectedUnit: any = this.uomFormGroup.value.Measurement;

    const storeSelectedList: any[] = [...Array.from(this.storeSelectedSet)];

    const dto = {
      MerchantId: this.data.merchantId,
      StoreId: this.data.store.Id,
      Name: this.productDetailFormGroup.value.Name,
      ImageUrl: this.productDetailFormGroup.value.ImageUrl,
      Description: this.productDetailFormGroup.value.Description,
      SKU: this.productDetailFormGroup.value.SKU || '',
      CategoryId: selectedCategory.Id,
      SubcategoryId: selectedSubcategory?.Id || null,
      Tags: selectedTags
        ? selectedTags.map((tag: ProductTagModel) => tag.Id)
        : [],
      ExcludeProductFromStores: storeSelectedList
        ? storeSelectedList.map((store: any) => store.Id)
        : [],
      Items: [
        {
          CostPrice: this.pricingFormGroup.value.CostPrice || 0,
          SellingPrice: this.pricingFormGroup.value.SellingPrice,
          UseVat: this.pricingFormGroup.value.IsVatable,
          Vat: this.pricingFormGroup.value.VatAmount || 0,
          Quantity: this.uomFormGroup.value.Units || 1,
          PublishedPrice: this.pricingFormGroup.value.DisplayedPrice?.toString().replace(',', ''),
          UnitId: selectedUnit.value,
          // Quantity: this.product.Quantity,// TODO BELOW (FOR Approved)
          // DefaultAmount: this.product.DefaultAmount,// TODO BELOW
          // LowInventoryQuantity: this.product.LowInventoryQuantity, //TODO
          // LowInventoryEmail: this.product.LowInventoryEmail,
          // SupplierName: this.product.SupplierName,
          // ReduceQuantityDailyStockLevel:
          //   this.product.ReduceQuantityDailyStockLevel,
          // StockLevelValue: this.product.StockLevelValue,
        },
      ],
    };

    this.productService
      .createProduct(dto, this.messageService)
      .then((data) => {
        this.loading = false;

        if (!data) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to add product. Please try again later.',
          });
          return;
        }

        if (!data.Success) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: data.ErrorMessage || 'Unable to add product. Please try again later.',
          });
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Product added successfully!',
        });
        this.dialogRef.close(UploadType.Uploaded);
      })
      .catch((err) => {
        this.loading = false;

        // Check if it's a network error
        if (!navigator.onLine) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No internet connection. Please try again.',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to add product. Please try again later.',
          });
        }
      });
  }

  private validateAllForms(): boolean {
    // Mark all fields as touched to show validation errors
    this.productDetailFormGroup.markAllAsTouched();
    this.pricingFormGroup.markAllAsTouched();
    this.uomFormGroup.markAllAsTouched();

    // Check if all forms are valid
    const isProductDetailValid = this.productDetailFormGroup.valid;
    const isPricingValid = this.pricingFormGroup.valid;
    const isUomValid = this.uomFormGroup.valid;

    if (!isProductDetailValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required product details.',
      });
      return false;
    }

    if (!isPricingValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required pricing information.',
      });
      return false;
    }

    if (!isUomValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required UOM information.',
      });
      return false;
    }

    return true;
  }
}
